import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiFetch } from "./client";
import type {
  AuditEntry,
  AuthConfig,
  BrowseResult,
  Catalog,
  ContextHelp,
  CredentialInfo,
  CredentialKind,
  DeliveriesResponse,
  DeliveryRecord,
  PlatformStatus,
  RoutePreview,
  SinkKind,
  SinkSpec,
  SubscriptionSpec,
  SubscriptionsInfo,
  TenantsInfo,
  TestSendResponse,
  DerivedList,
  DerivedPreview,
  FileContent,
  FileKind,
  FlightManifest,
  FlightRecord,
  FrameBinding,
  FrameDetail,
  FrameFileSummary,
  FrameInfo,
  FramesList,
  InputsInfo,
  ParameterDef,
  TestDecodeResult,
  LanesResponse,
  OverlayResponse,
  PreviewResponse,
  Problem,
  ProjectFile,
  ProjectSummary,
  ReportDef,
  ReportEntry,
  RunRecord,
  SeriesResponse,
  User,
  WorkspaceInfo,
} from "./types";

export const keys = {
  me: ["me"] as const,
  workspace: ["workspace"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  files: (id: string, kind?: FileKind) => ["files", id, kind ?? "*"] as const,
  file: (id: string, path: string) => ["file", id, path] as const,
  frame: (id: string) => ["frame", id] as const,
  frames: (id: string) => ["frames", id] as const,
  frameDetail: (id: string, name: string) => ["frame-detail", id, name] as const,
  frameParam: (id: string, name: string, mnemonic: string) => ["frame-param", id, name, mnemonic] as const,
  derived: (id: string) => ["derived", id] as const,
  inputs: (id: string) => ["inputs", id] as const,
  catalog: (id: string) => ["catalog", id] as const,
  reports: (id: string) => ["reports", id] as const,
  flights: ["flights"] as const,
  browse: (root: number, path: string) => ["browse", root, path] as const,
  runs: (project?: string) => ["runs", project ?? "*"] as const,
  run: (id: string) => ["run", id] as const,
  runFlight: (run: string, flight: string) => ["run-flight", run, flight] as const,
  lanes: (run: string, flight: string) => ["lanes", run, flight] as const,
  series: (flight: string, project: string, params: string, points: number) =>
    ["series", flight, project, params, points] as const,
  contextHelp: ["context-help"] as const,
  tenants: ["tenants"] as const,
  authConfig: ["auth-config"] as const,
  platform: ["platform-status"] as const,
  credentials: ["credentials"] as const,
  credentialKinds: ["credential-kinds"] as const,
  sinkKinds: ["sink-kinds"] as const,
  subscriptions: (id: string) => ["subscriptions", id] as const,
  routePreview: (id: string, run?: string) => ["route-preview", id, run ?? "*"] as const,
  deliveries: (project?: string, run?: string, status?: string) => ["deliveries", project ?? "*", run ?? "*", status ?? "*"] as const,
  delivery: (id: string) => ["delivery", id] as const,
  audit: (opts: string) => ["audit", opts] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api.get<User>("/me"), staleTime: Infinity });
export const useWorkspace = () =>
  useQuery({ queryKey: keys.workspace, queryFn: () => api.get<WorkspaceInfo>("/workspace") });

// -- projects ------------------------------------------------------------------------------
export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: () => api.get<ProjectSummary[]>("/projects") });

export const useProject = (id: string | null) =>
  useQuery({
    queryKey: keys.project(id ?? ""),
    queryFn: () => api.get<ProjectSummary>(`/projects/${id}`),
    enabled: !!id,
  });

export const useProjectFiles = (id: string | null, kind?: FileKind) =>
  useQuery({
    queryKey: keys.files(id ?? "", kind),
    queryFn: () => api.get<ProjectFile[]>(`/projects/${id}/files${kind ? `?kind=${kind}` : ""}`),
    enabled: !!id,
  });

export const useProjectFile = (id: string | null, path: string | null) =>
  useQuery({
    queryKey: keys.file(id ?? "", path ?? ""),
    queryFn: () => api.get<FileContent>(`/projects/${id}/files/${path}`),
    enabled: !!id && !!path,
    staleTime: Infinity,
  });

export function useSaveFile(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, content }: { path: string; content: string }) =>
      api.put<{ path: string; kind: FileKind; size: number; problems?: Problem[] }>(
        `/projects/${projectId}/files/${path}`,
        { content },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["files", projectId] });
      qc.invalidateQueries({ queryKey: keys.file(projectId ?? "", vars.path) });
      qc.invalidateQueries({ queryKey: keys.project(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.catalog(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.inputs(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.reports(projectId ?? "") });
      if (vars.path.startsWith("derived/")) {
        qc.invalidateQueries({ queryKey: keys.derived(projectId ?? "") });
        qc.invalidateQueries({ queryKey: ["series"] });
      }
    },
  });
}

export function useDeleteFile(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => api.del<void>(`/projects/${projectId}/files/${path}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["files", projectId] });
      qc.invalidateQueries({ queryKey: keys.project(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.catalog(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.derived(projectId ?? "") });
    },
  });
}

export const useFrame = (id: string | null) =>
  useQuery({ queryKey: keys.frame(id ?? ""), queryFn: () => api.get<FrameInfo>(`/projects/${id}/frame`), enabled: !!id });

// -- frame editor ----------------------------------------------------------------------------
export const useFrames = (id: string | null) =>
  useQuery({ queryKey: keys.frames(id ?? ""), queryFn: () => api.get<FramesList>(`/projects/${id}/frames`), enabled: !!id });

export const useFrameDetail = (id: string | null, name: string | null) =>
  useQuery({
    queryKey: keys.frameDetail(id ?? "", name ?? ""),
    queryFn: () => api.get<FrameDetail>(`/projects/${id}/frames/${encodeURIComponent(name ?? "")}`),
    enabled: !!id && !!name,
  });

export const useFrameParameter = (id: string | null, name: string | null, mnemonic: string | null) =>
  useQuery({
    queryKey: keys.frameParam(id ?? "", name ?? "", mnemonic ?? ""),
    queryFn: () =>
      api.get<{ parameter: ParameterDef; original: ParameterDef | null; is_override: boolean; in_sidecar: boolean }>(
        `/projects/${id}/frames/${encodeURIComponent(name ?? "")}/parameters/${encodeURIComponent(mnemonic ?? "")}`,
      ),
    enabled: !!id && !!name && !!mnemonic,
    retry: false,
  });

function invalidateFrames(qc: ReturnType<typeof useQueryClient>, projectId: string | null, params = true) {
  qc.invalidateQueries({ queryKey: ["frames", projectId] });
  qc.invalidateQueries({ queryKey: ["frame-detail", projectId] });
  if (params) qc.invalidateQueries({ queryKey: ["frame-param", projectId] });
  qc.invalidateQueries({ queryKey: keys.frame(projectId ?? "") });
  qc.invalidateQueries({ queryKey: keys.catalog(projectId ?? "") });
  qc.invalidateQueries({ queryKey: keys.project(projectId ?? "") });
  qc.invalidateQueries({ queryKey: ["files", projectId] });
  qc.invalidateQueries({ queryKey: ["series"] });
  qc.invalidateQueries({ queryKey: keys.flights });
}

export function useSaveFrameParameter(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frame, mnemonic, parameter, renameFrom }: { frame: string; mnemonic: string; parameter: Partial<ParameterDef>; renameFrom?: string }) =>
      api.put<{ parameter: ParameterDef; origin: string }>(
        `/projects/${projectId}/frames/${encodeURIComponent(frame)}/parameters/${encodeURIComponent(mnemonic)}`,
        { parameter, rename_from: renameFrom ?? null },
      ),
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export function useDeleteFrameParameter(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frame, mnemonic }: { frame: string; mnemonic: string }) =>
      api.del<{ reverted_to: ParameterDef | null; deleted: boolean }>(
        `/projects/${projectId}/frames/${encodeURIComponent(frame)}/parameters/${encodeURIComponent(mnemonic)}`,
      ),
    onSuccess: (data, vars) => {
      invalidateFrames(qc, projectId, false);
      const key = keys.frameParam(projectId ?? "", vars.frame, vars.mnemonic);
      // a deleted parameter has no definition to refetch; a reverted one does
      if (data.deleted) qc.removeQueries({ queryKey: key });
      else qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useCreateFrame(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; words_per_second?: number; description?: string; copy_from?: string; set_default?: boolean }) =>
      api.post<FrameFileSummary>(`/projects/${projectId}/frames`, body),
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export function useUpdateFrameMeta(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frame, ...body }: { frame: string; name?: string; description?: string; words_per_second?: number; aircraft_manufacturer?: string; aircraft_model?: string }) =>
      api.put<unknown>(`/projects/${projectId}/frames/${encodeURIComponent(frame)}`, body),
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export function useDeleteFrame(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (frame: string) => api.del<void>(`/projects/${projectId}/frames/${encodeURIComponent(frame)}`),
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export function useImportPrm(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name, setDefault, tails, overwrite }: { file: File; name?: string; setDefault?: boolean; tails?: string[]; overwrite?: boolean }) => {
      const form = new FormData();
      form.append("file", file, file.name);
      if (name) form.append("name", name);
      form.append("set_default", setDefault ? "true" : "false");
      form.append("tails", (tails ?? []).join(","));
      form.append("overwrite", overwrite ? "true" : "false");
      return apiFetch<FrameFileSummary & { problems: string[]; parameter_count: number }>(`/projects/${projectId}/frames:import-prm`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export function useSaveBindings(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { default: string | null; bindings: FrameBinding[] }) =>
      api.put<{ default: string | null; bindings: FrameBinding[] }>(`/projects/${projectId}/frames:bindings`, body),
    onSuccess: () => invalidateFrames(qc, projectId),
  });
}

export const testDecode = (projectId: string, body: { flight_id: string; frame: string; parameter: Partial<ParameterDef>; points?: number }) =>
  api.post<TestDecodeResult>(`/projects/${projectId}/frames:test-decode`, body);

// -- derived parameters ----------------------------------------------------------------------
export const useDerived = (id: string | null) =>
  useQuery({ queryKey: keys.derived(id ?? ""), queryFn: () => api.get<DerivedList>(`/projects/${id}/derived`), enabled: !!id });

export const previewDerived = (projectId: string, body: { flight_id: string; path: string; source: string; points?: number; only?: string[] }) =>
  api.post<DerivedPreview>(`/projects/${projectId}/derived/preview`, body);

export const useInputs = (id: string | null) =>
  useQuery({ queryKey: keys.inputs(id ?? ""), queryFn: () => api.get<InputsInfo>(`/projects/${id}/inputs`), enabled: !!id });

export const useCatalog = (id: string | null) =>
  useQuery({ queryKey: keys.catalog(id ?? ""), queryFn: () => api.get<Catalog>(`/projects/${id}/catalog`), enabled: !!id });

export const useReports = (id: string | null) =>
  useQuery({ queryKey: keys.reports(id ?? ""), queryFn: () => api.get<ReportEntry[]>(`/projects/${id}/reports`), enabled: !!id });

export function useSaveReport(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, report }: { name: string; report: ReportDef }) =>
      api.put<{ file: string; report: ReportDef }>(`/projects/${projectId}/reports/${encodeURIComponent(name)}`, report),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.reports(projectId ?? "") });
      qc.invalidateQueries({ queryKey: ["files", projectId] });
      qc.invalidateQueries({ queryKey: keys.project(projectId ?? "") });
    },
  });
}

export function useDeleteReport(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.del<void>(`/projects/${projectId}/reports/${encodeURIComponent(name)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.reports(projectId ?? "") });
      qc.invalidateQueries({ queryKey: keys.project(projectId ?? "") });
    },
  });
}

export const previewReport = (projectId: string, body: unknown) =>
  api.post<PreviewResponse>(`/projects/${projectId}/reports/preview`, body);

export const useContextHelp = () =>
  useQuery({
    queryKey: keys.contextHelp,
    queryFn: () => api.get<ContextHelp>(`/projects/_/reports/context-help`),
    staleTime: Infinity,
  });

export const lintSource = (projectId: string, path: string, source: string) =>
  api.post<{ problems: Problem[] }>(`/projects/${projectId}/lint`, { path, source });

// -- flights ---------------------------------------------------------------------------------
export const useFlights = () =>
  useQuery({ queryKey: keys.flights, queryFn: () => api.get<FlightRecord[]>("/flights") });

export const useFlightRoots = () =>
  useQuery({ queryKey: ["flight-roots"], queryFn: () => api.get<{ index: number; path: string }[]>("/flights/roots") });

export const useBrowse = (root: number, path: string, enabled = true) =>
  useQuery({
    queryKey: keys.browse(root, path),
    queryFn: () => api.get<BrowseResult>(`/flights/browse?root=${root}&path=${encodeURIComponent(path)}`),
    enabled,
  });

export function useRegisterFlights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bodies: { root?: string; path: string; tail?: string; date?: string }[]) =>
      api.post<FlightRecord[]>("/flights/register-many", bodies),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.flights }),
  });
}

export function useUploadFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, tail, date, folderName }: { files: File[]; tail?: string; date?: string; folderName?: string }) => {
      const form = new FormData();
      for (const f of files) form.append("files", f, f.name);
      if (tail) form.append("tail", tail);
      if (date) form.append("date", date);
      if (folderName) form.append("folder_name", folderName);
      return apiFetch<FlightRecord>("/flights/upload", { method: "POST", body: form });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.flights }),
  });
}

export function useUpdateFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; tail?: string; date?: string; notes?: string; tags?: string[]; fields?: Record<string, unknown> }) =>
      apiFetch<FlightRecord>(`/flights/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.flights }),
  });
}

export function useDeleteFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/flights/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.flights }),
  });
}

export const useSeries = (flightId: string | null, projectId: string | null, params: string[], points = 2000) => {
  const p = params.join(",");
  return useQuery({
    queryKey: keys.series(flightId ?? "", projectId ?? "", p, points),
    queryFn: () =>
      api.get<SeriesResponse>(
        `/flights/${flightId}/series?project=${projectId}&params=${encodeURIComponent(p)}&points=${points}`,
      ),
    enabled: !!flightId && !!projectId && params.length > 0,
    staleTime: 5 * 60_000,
  });
};

export const fetchValuesAt = (flightId: string, projectId: string, t: number, params: string[]) =>
  api.get<{ t: number; time_utc: string | null; values: Record<string, number | null> }>(
    `/flights/${flightId}/at?project=${projectId}&t=${t}&params=${encodeURIComponent(params.join(","))}`,
  );

// -- runs --------------------------------------------------------------------------------------
export const useRuns = (projectId: string | null) =>
  useQuery({
    queryKey: keys.runs(projectId ?? undefined),
    queryFn: () => api.get<RunRecord[]>(`/runs${projectId ? `?project=${projectId}` : ""}`),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "queued" || r.status === "running") ? 1000 : false),
  });

export const useRun = (runId: string | null) =>
  useQuery({
    queryKey: keys.run(runId ?? ""),
    queryFn: () => api.get<RunRecord>(`/runs/${runId}`),
    enabled: !!runId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "running" ? 800 : false;
    },
  });

export interface RunRequest {
  project: string;
  flights: string[];
  trace?: "off" | "bool" | "all";
  input_overrides?: Record<string, unknown>;
  deliver_to?: string;
  deliver?: boolean;
  source_override?: { path: string; source: string };
  only_triggers?: string[];
  label?: string;
}

export function useDeliverRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, flightId }: { runId: string; flightId?: string }) =>
      api.post<{ run_id: string; queued: DeliveryRecord[]; skipped: { flight: string; filename: string; reason: string }[] }>(
        `/runs/${runId}/deliver${flightId ? `?flight_id=${encodeURIComponent(flightId)}` : ""}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deliveries"] }),
  });
}

export function useSubmitRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: RunRequest) => api.post<RunRecord>("/runs", req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}

export function useCancelRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ cancelled: boolean }>(`/runs/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}

export function useDeleteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/runs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });
}

export const useRunFlight = (runId: string | null, flightId: string | null) =>
  useQuery({
    queryKey: keys.runFlight(runId ?? "", flightId ?? ""),
    queryFn: () => api.get<FlightManifest>(`/runs/${runId}/flights/${flightId}`),
    enabled: !!runId && !!flightId,
  });

export const useLanes = (runId: string | null, flightId: string | null) =>
  useQuery({
    queryKey: keys.lanes(runId ?? "", flightId ?? ""),
    queryFn: () => api.get<LanesResponse>(`/runs/${runId}/flights/${flightId}/lanes`),
    enabled: !!runId && !!flightId,
    retry: false,
    staleTime: Infinity,
  });

export const fetchOverlay = (runId: string, flightId: string, t: number, trigger?: string) =>
  api.get<OverlayResponse>(`/runs/${runId}/flights/${flightId}/at?t=${t}${trigger ? `&trigger=${trigger}` : ""}`);

export const artifactUrl = (runId: string, flightId: string, filename: string, download = false) =>
  `/api/runs/${runId}/flights/${flightId}/artifacts/${encodeURIComponent(filename)}${download ? "?download=1" : ""}`;

export const fetchArtifact = (runId: string, flightId: string, filename: string) =>
  api.get<string>(`/runs/${runId}/flights/${flightId}/artifacts/${encodeURIComponent(filename)}`);

// -- tenants / auth / platform -------------------------------------------------------------------
export const useTenants = () => useQuery({ queryKey: keys.tenants, queryFn: () => api.get<TenantsInfo>("/auth/tenants") });

export const useAuthConfig = () =>
  useQuery({ queryKey: keys.authConfig, queryFn: () => api.get<AuthConfig>("/auth/config"), staleTime: Infinity });

export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenant: string) => api.post<{ active: string }>("/auth/tenant", { tenant }),
    onSuccess: () => {
      // everything the user sees is tenant scoped
      qc.clear();
    },
  });
}

export const usePlatformStatus = (enabled = true) =>
  useQuery({ queryKey: keys.platform, queryFn: () => api.get<PlatformStatus>("/admin/status"), enabled, staleTime: 30_000 });

export const useAudit = (opts: { action?: string; user?: string; hours?: number; allTenants?: boolean; limit?: number }, enabled = true) => {
  const q = new URLSearchParams();
  if (opts.action) q.set("action", opts.action);
  if (opts.user) q.set("user", opts.user);
  if (opts.hours) q.set("hours", String(opts.hours));
  if (opts.allTenants) q.set("all_tenants", "true");
  if (opts.limit) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return useQuery({
    queryKey: keys.audit(qs),
    queryFn: () => api.get<{ entries: AuditEntry[]; tenant: string | null }>(`/audit${qs ? `?${qs}` : ""}`),
    enabled,
  });
};

// -- credentials ----------------------------------------------------------------------------------
export const useCredentials = () => useQuery({ queryKey: keys.credentials, queryFn: () => api.get<CredentialInfo[]>("/credentials") });

export const useCredentialKinds = () =>
  useQuery({
    queryKey: keys.credentialKinds,
    queryFn: () => api.get<{ kinds: Record<string, CredentialKind> }>("/credentials/kinds"),
    staleTime: Infinity,
  });

export const fetchCredential = (id: string) => api.get<CredentialInfo>(`/credentials/${encodeURIComponent(id)}`);

export function useSaveCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, create, ...body }: { id: string; create: boolean; name: string; kind: string; fields: Record<string, unknown> }) =>
      create
        ? api.post<CredentialInfo>("/credentials", { id, ...body })
        : api.put<CredentialInfo>(`/credentials/${encodeURIComponent(id)}`, { id, ...body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/credentials/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export const testCredential = (id: string) => api.post<{ ok: boolean; detail: string; sink?: string }>(`/credentials/${encodeURIComponent(id)}/test`);

// -- subscriptions ----------------------------------------------------------------------------------
export const useSubscriptions = (projectId: string | null) =>
  useQuery({
    queryKey: keys.subscriptions(projectId ?? ""),
    queryFn: () => api.get<SubscriptionsInfo>(`/projects/${projectId}/subscriptions`),
    enabled: !!projectId,
    retry: false,
  });

export const useSinkKinds = () =>
  useQuery({
    queryKey: keys.sinkKinds,
    queryFn: () => api.get<{ sinks: Record<string, SinkKind> }>(`/projects/_/subscriptions/kinds`),
    staleTime: Infinity,
  });

export function useSaveSubscriptions(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sinks?: SinkSpec[]; subscriptions?: SubscriptionSpec[]; yaml?: string }) =>
      api.put<SubscriptionsInfo>(`/projects/${projectId}/subscriptions`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.subscriptions(projectId ?? "") });
      qc.invalidateQueries({ queryKey: ["route-preview", projectId] });
      qc.invalidateQueries({ queryKey: ["files", projectId] });
    },
  });
}

export const validatePredicate = (projectId: string, predicate: string) =>
  api.post<{ ok: boolean; error?: string }>(`/projects/${projectId}/subscriptions/validate-predicate`, { predicate });

export const testSend = (
  projectId: string,
  body: { sink?: string; sink_spec?: SinkSpec; run_id?: string; flight_id?: string; connectivity_only?: boolean },
) => api.post<TestSendResponse>(`/projects/${projectId}/subscriptions/test-send`, body);

export const useRoutePreview = (projectId: string | null, runId?: string | null) =>
  useQuery({
    queryKey: keys.routePreview(projectId ?? "", runId ?? undefined),
    queryFn: () => api.get<RoutePreview>(`/projects/${projectId}/subscriptions/preview${runId ? `?run_id=${runId}` : ""}`),
    enabled: !!projectId,
  });

// -- deliveries ------------------------------------------------------------------------------------
export const useDeliveries = (opts: { project?: string | null; run?: string | null; status?: string | null; limit?: number }, enabled = true) => {
  const q = new URLSearchParams();
  if (opts.project) q.set("project", opts.project);
  if (opts.run) q.set("run", opts.run);
  if (opts.status) q.set("status", opts.status);
  if (opts.limit) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return useQuery({
    queryKey: keys.deliveries(opts.project ?? undefined, opts.run ?? undefined, opts.status ?? undefined),
    queryFn: () => api.get<DeliveriesResponse>(`/deliveries${qs ? `?${qs}` : ""}`),
    enabled,
    refetchInterval: (q) =>
      q.state.data?.deliveries.some((d) => d.status === "queued" || d.status === "delivering") ? 1500 : false,
  });
};

export const useDelivery = (id: string | null) =>
  useQuery({
    queryKey: keys.delivery(id ?? ""),
    queryFn: () => api.get<DeliveryRecord>(`/deliveries/${id}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "delivering" ? 1500 : false;
    },
  });

export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<DeliveryRecord>(`/deliveries/${id}/retry`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: keys.delivery(id) });
    },
  });
}
